import { BigNumber } from '@ethersproject/bignumber'
import { CallStateResult, useSingleContractMultipleData } from 'lib/hooks/multicall'
import { useMemo } from 'react'
import { PositionDetails } from 'types/position'

import { WALLET_ROLES, WalletRole } from '../config/walletAccess'
import { useV3NFTPositionManagerContract } from './useContract'

export interface PositionWithOwner extends PositionDetails {
  owner: string
}

interface UseAllAdminPositionsResult {
  loading: boolean
  positions: PositionWithOwner[] | undefined
}

export function useAllAdminPositions(): UseAllAdminPositionsResult {
  const adminWallets = useMemo(() => {
    const all = [...WALLET_ROLES[WalletRole.ADMIN], ...WALLET_ROLES[WalletRole.PROVIDER]]
    return [...new Set(all.map((a) => a.toLowerCase()))].map(
      (lower) => all.find((a) => a.toLowerCase() === lower) ?? lower
    )
  }, [])
  const positionManager = useV3NFTPositionManagerContract()

  const balanceArgs = useMemo(() => adminWallets.map((wallet) => [wallet]), [adminWallets])
  const balanceResults = useSingleContractMultipleData(positionManager, 'balanceOf', balanceArgs)
  const balancesLoading = useMemo(() => balanceResults.some(({ loading }) => loading), [balanceResults])

  const tokenIdArgs = useMemo(() => {
    if (balancesLoading) return []
    const args: [string, number][] = []
    adminWallets.forEach((wallet, walletIndex) => {
      const balance = balanceResults[walletIndex]?.result?.[0]?.toNumber() ?? 0
      for (let i = 0; i < balance; i++) {
        args.push([wallet, i])
      }
    })
    return args
  }, [adminWallets, balanceResults, balancesLoading])

  const tokenIdToWallet = useMemo(() => {
    return tokenIdArgs.map(([wallet]) => wallet)
  }, [tokenIdArgs])

  const tokenIdResults = useSingleContractMultipleData(positionManager, 'tokenOfOwnerByIndex', tokenIdArgs)
  const tokenIdsLoading = useMemo(() => tokenIdResults.some(({ loading }) => loading), [tokenIdResults])

  const tokenIds = useMemo(() => {
    return tokenIdResults
      .map(({ result }) => (result ? BigNumber.from(result[0]) : undefined))
      .filter((id): id is BigNumber => !!id)
  }, [tokenIdResults])

  const positionArgs = useMemo(() => tokenIds.map((id) => [BigNumber.from(id)]), [tokenIds])
  const positionResults = useSingleContractMultipleData(positionManager, 'positions', positionArgs)
  const positionsLoading = useMemo(() => positionResults.some(({ loading }) => loading), [positionResults])

  const positions = useMemo(() => {
    if (balancesLoading || tokenIdsLoading || positionsLoading) return undefined
    if (tokenIds.length === 0) return []

    return positionResults
      .map((call, i) => {
        const result = call.result as CallStateResult | undefined
        if (!result) return undefined

        const poolKey = result.poolKey
        if (!poolKey) return undefined

        return {
          tokenId: tokenIds[i],
          fee: poolKey.fee,
          version: poolKey.version,
          feeGrowthInside0LastX128: result.feeGrowthInside0LastX128,
          feeGrowthInside1LastX128: result.feeGrowthInside1LastX128,
          liquidity: result.liquidity,
          nonce: result.nonce,
          operator: result.operator,
          tickLower: result.tickLower,
          tickUpper: result.tickUpper,
          token0: poolKey.token0,
          token1: poolKey.token1,
          tokensOwed0: result.tokensOwed0,
          tokensOwed1: result.tokensOwed1,
          owner: tokenIdToWallet[i],
        } as PositionWithOwner
      })
      .filter((p): p is PositionWithOwner => !!p)
  }, [balancesLoading, tokenIdsLoading, positionsLoading, positionResults, tokenIds, tokenIdToWallet])

  return {
    loading: balancesLoading || tokenIdsLoading || positionsLoading,
    positions,
  }
}

/** Group positions by pool key (token0 + token1 + fee) */
export function groupPositionsByPool(positions: PositionWithOwner[]): Map<string, PositionWithOwner[]> {
  const map = new Map<string, PositionWithOwner[]>()
  for (const pos of positions) {
    const key = `${pos.token0}-${pos.token1}-${pos.fee}`
    const existing = map.get(key) ?? []
    existing.push(pos)
    map.set(key, existing)
  }
  return map
}
